'use client'

import { useState } from 'react'
import Head from 'next/head'
import { FaChevronDown } from 'react-icons/fa'
import { slugify } from '@/lib/slugify'

interface FAQItem {
  question: string
  answer: string
}

interface FAQProps {
  faqs?: FAQItem[]
  city?: string
  sectionId?: string
}

export default function FAQ({ faqs = [], city, sectionId }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  if (faqs.length === 0) {
    return null
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const id = sectionId || slugify(`Häufig gestellte Fragen - Autoverwertung ${city || 'Kaiserslautern '}`);

  return (
    <>
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      </Head>
      <section id={id} className="py-12 md:py-16 lg:py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              <a href={`#${id}`} className="group inline-flex items-center gap-2 hover:text-primary-dark justify-center">
                Häufig gestellte Fragen - Autoverwertung {city || "Kaiserslautern "}
                <span className="text-primary-dark opacity-0 transition-opacity duration-200 group-hover:opacity-100">#</span>
              </a>
            </h2>
            <p className="text-lg text-gray-600">
              Finden Sie schnell Antworten auf Ihre Fragen rund um Autoverwertung in {city || "Kaiserslautern "} – von Ablauf über Kosten bis hin zu Umweltaspekten. Alle wichtigen Infos auf einen Blick!
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden hover:border-primary transition-colors duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 sm:py-5 flex items-center justify-between bg-white  transition-colors duration-300"
                >
                  <h3 className="text-left font-semibold text-gray-900 sm:text-lg">
                    {faq.question}
                  </h3>
                  <FaChevronDown
                    className={`text-primary flex-shrink-0 ml-4 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {openIndex === index && (
                  <div className="px-6 py-4 sm:py-5  border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    </>
  )
}